import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import {
  extractGameplan,
  parseDISC,
  parseLeadEnrichment,
  parseSignals,
  type ParsedGameplan,
  type DISCProfile,
  type LeadEnrichment,
  type ConversationSignal,
} from '@/lib/intelligence-helpers';
import { isLiveDraft, pickMostRecentDraft, type LiveDraftStatus } from '@/lib/crm/live-draft';
import { excludeConfirmedTrashedThreads } from '@/lib/crm/thread-lifecycle';
import type { Tables } from '@/types/database';

// ─────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────

/** Exactly the `os_linkedin_drafts` columns this hook reads, picked from the
 * generated Row type so nullability can never silently drift from the real
 * schema — e.g. `methodology_used`/`confidence` are `null` for every fresh
 * `awaiting_draft` row (api/os/webhooks/heyreach.ts:951-954), and a hand-
 * written `string` here would suppress that instead of forcing it to be
 * handled. Not `DraftListItem` (from useLinkedInDrafts): that type's
 * `status` union omits `awaiting_draft` and it carries no `is_superseded`
 * — both required for the live-draft contract. */
type DossierDraftRow = Pick<
  Tables<'os_linkedin_drafts'>,
  | 'id' | 'thread_id' | 'lead_name' | 'lead_title' | 'lead_company' | 'lead_linkedin_url'
  | 'incoming_message' | 'incoming_at' | 'draft_response' | 'edited_response'
  | 'methodology_used' | 'confidence' | 'status' | 'is_superseded' | 'created_at'
  | 'heyreach_campaign_id' | 'heyreach_lead_id'
  | 'analysis_report' | 'strategy' | 'decision_trace'
  | 'sent_at' | 'approved_at'
>;

/** A JSONB column narrows to a real object, never a JSON primitive/array —
 * the intelligence-helpers parsers all expect `Record<string, unknown> |
 * null`, not the generated `Json | null`. Runtime-checked, not a blind cast. */
function asJsonRecord(value: Tables<'os_linkedin_drafts'>['analysis_report']): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export interface LivePendingDraft {
  id: string;
  status: LiveDraftStatus;
  threadId: string | null;
  /** True when `threadId` resolves to a CONFIRMED-Trashed thread — the
   * "Review Draft" continuation must not navigate there: `useThreadById`
   * queries `os_conversation_inbox`, which excludes soft-deleted threads
   * (025_soft_delete_threads.sql), so it would deterministically dead-end
   * at "Conversation not found". `threadId === null` (F1: a webhook
   * thread-creation failure, not a Trashed thread) is never "deleted". */
  isThreadDeleted: boolean;
}

export interface TimelineEntry {
  id: string;
  incomingMessage: string;
  incomingAt: string;
  draftResponse: string;
  editedResponse: string | null;
  status: string;
  /** Superseded rows are historical, never "currently live" regardless of status. */
  isSuperseded: boolean;
  methodology: string;
  confidence: number;
  sentAt: string | null;
  approvedAt: string | null;
  createdAt: string;
}

export interface LeadProfileData {
  leadName: string;
  leadTitle: string | null;
  leadCompany: string | null;
  leadLinkedinUrl: string | null;
  heyreachCampaignId: string;
  firstContact: string;
  lastActivity: string;
  exchangeCount: number;
}

export interface LeadDossierData {
  profile: LeadProfileData;
  /** Canonical display name for the campaign carried by the same draft row
   * that supplies `profile.heyreachCampaignId` and `gameplan` — the row the
   * CTAs act on (`livePendingDraft`), degrading to the lead-global newest row
   * only when the lead has no live draft. `null` is an explicit unresolved
   * state; callers must render a visible fallback. */
  campaignName: string | null;
  gameplan: ParsedGameplan;
  disc: DISCProfile | null;
  enrichment: LeadEnrichment;
  signals: Array<ConversationSignal & { messageIndex: number; timestamp: string }>;
  timeline: TimelineEntry[];
  approachesUsed: Array<{ methodology: string; count: number; responseCount: number }>;
  /** The most recent live (awaiting_draft/pending_approval, not superseded,
   * non-empty message) draft, if any — what the "Review Draft" action
   * targets. `null` means no live draft, not merely no `pending_approval`
   * one. A "has a pending draft" boolean is exactly `livePendingDraft !==
   * null`, derived by the caller — not duplicated as a second field here. */
  livePendingDraft: LivePendingDraft | null;
  /** The queue-carried draft/thread pair stopped being live between click
   * and dossier load, so `livePendingDraft` uses the normal global winner.
   * The page surfaces this instead of silently switching sender context. */
  didFallbackFromCarriedDraft: boolean;
}

export interface LeadDossierDraftIdentity {
  draftId: string;
  threadId: string | null;
}

// ─────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────

/** The ONE definition of this cache key, both variants. The bare and carried-draft keys are
 * DIFFERENT cache entries, so a test that warms one and reads the other proves nothing — exported
 * so the rename fan-out test asserts against the key the hook actually uses (DIFF r2 F4). */
export const leadDossierQueryKey = (
  userId: string | null,
  organizationId: string | null,
  heyreachLeadId: string | undefined,
  carriedDraft: LeadDossierDraftIdentity | null
): readonly unknown[] =>
  carriedDraft
    ? ['lead-dossier', userId, organizationId, heyreachLeadId, carriedDraft.draftId, carriedDraft.threadId]
    : ['lead-dossier', userId, organizationId, heyreachLeadId];

export const useLeadDossier = (
  heyreachLeadId: string | undefined,
  carriedDraft: LeadDossierDraftIdentity | null = null
) => {
  const { user, organizationId } = useAuth();
  const userId = user?.id ?? null;

  return useQuery<LeadDossierData | null>({
    queryKey: leadDossierQueryKey(userId, organizationId, heyreachLeadId, carriedDraft),
    queryFn: async () => {
      if (!heyreachLeadId || !organizationId) return null;

      // r7-F1: heyreach_lead_id is a HeyReach-GLOBAL identifier (S9a r6-F1) — the
      // same id can exist under two different organizations. This dossier is the
      // click-through destination from the org-scoped campaign leads list
      // (useCampaignLeads / get_campaign_leads_v2), so without this filter a
      // membership transition (RLS visibility briefly exceeding the session's
      // context org — see useCampaignPulse) could merge a foreign organization's
      // drafts into this profile/timeline/gameplan, and its CTA could act on a
      // foreign-org draft. RLS still applies on top; this is defense-in-depth,
      // matching the same client-side org filter already used by
      // useCampaignPulse/useCampaignDetail.
      const { data, error } = await supabase
        .from('os_linkedin_drafts')
        .select(`
          id, thread_id, lead_name, lead_title, lead_company, lead_linkedin_url,
          incoming_message, incoming_at, draft_response, edited_response,
          methodology_used, confidence, status, is_superseded, created_at,
          heyreach_campaign_id, heyreach_lead_id,
          analysis_report, strategy, decision_trace,
          sent_at, approved_at
        `)
        .eq('heyreach_lead_id', heyreachLeadId)
        .eq('organization_id', organizationId)
        .order('incoming_at', { ascending: true });

      if (error) throw error;
      if (!data || data.length === 0) return null;

      const rawDrafts = data as unknown as DossierDraftRow[];

      // Normalize nullable presentation fields ONCE, here — every reader
      // below sees these values, never the raw nullable column. A freshly
      // saved `awaiting_draft` row has `methodology_used`/`confidence` NULL
      // (no AI generation ran yet: api/os/webhooks/heyreach.ts:951-954);
      // `status`/`created_at` are NULL-able in the generated Row type even
      // though no current write path produces that — normalizing here means
      // it degrades to an honest placeholder instead of a runtime crash on
      // a future write path that does.
      const drafts = rawDrafts.map((d) => ({
        ...d,
        methodology_used: d.methodology_used ?? 'unknown',
        confidence: d.confidence ?? 0,
        status: d.status ?? 'unknown',
        created_at: d.created_at ?? d.incoming_at,
      }));

      const latest = drafts[drafts.length - 1];
      if (!latest) return null;

      // Build timeline
      const timeline: TimelineEntry[] = drafts.map((d) => ({
        id: d.id,
        incomingMessage: d.incoming_message,
        incomingAt: d.incoming_at,
        draftResponse: d.draft_response,
        editedResponse: d.edited_response,
        status: d.status,
        isSuperseded: d.is_superseded === true,
        methodology: d.methodology_used,
        confidence: d.confidence,
        sentAt: d.sent_at || null,
        approvedAt: d.approved_at || null,
        createdAt: d.created_at,
      }));

      // Most recent live draft — what "Review Draft" targets. isLiveDraft is
      // the SAME predicate the Reply Queue itself enforces
      // (src/lib/crm/live-draft.ts) — including the non-empty-message rule
      // this dossier previously omitted. pickMostRecentDraft is the SAME
      // winner rule the queue's own dedupe uses (incoming_at DESC, id ASC
      // tiebreak) — not "last array element", which disagreed with the
      // queue whenever two live drafts shared a tied incoming_at.
      const liveDrafts = drafts.filter(isLiveDraft);

      // F4: exclude CONFIRMED-Trashed-thread candidates BEFORE picking a
      // winner — the exact operation order the queue uses
      // (fetchQualifyingDraftRows -> excludeConfirmedTrashedThreads ->
      // dedupeByLatestIncoming in useReplyQueue.ts). One lead can have live
      // drafts on more than one thread (one thread per org+lead+campaign,
      // not per lead — supabase/migrations/017_conversation_threads.sql), so
      // picking a winner across ALL threads first and only THEN checking
      // Trash (the old order) let a newer draft on a Trashed thread outrank
      // an older, still-actionable draft on a live thread — disagreeing with
      // what the queue showed for the same lead. Only when EVERY live draft
      // is Trashed do we fall back to the newest Trashed one, purely to
      // explain the state — it never outranks a queue-eligible candidate.
      const queueEligibleDrafts = await excludeConfirmedTrashedThreads(liveDrafts, organizationId);
      const carriedLiveDraft = carriedDraft
        ? liveDrafts.find(
            (draft) => draft.id === carriedDraft.draftId && draft.thread_id === carriedDraft.threadId
          ) ?? null
        : null;
      const latestLiveDraft = carriedLiveDraft ?? (queueEligibleDrafts.length > 0
        ? pickMostRecentDraft(queueEligibleDrafts)
        : pickMostRecentDraft(liveDrafts));
      const queueEligibleIds = new Set(queueEligibleDrafts.map((d) => d.id));
      const isThreadDeleted = latestLiveDraft !== null && !queueEligibleIds.has(latestLiveDraft.id);
      const didFallbackFromCarriedDraft = carriedDraft !== null && carriedLiveDraft === null;

      const livePendingDraft: LivePendingDraft | null = latestLiveDraft
        ? { id: latestLiveDraft.id, status: latestLiveDraft.status as LiveDraftStatus, threadId: latestLiveDraft.thread_id, isThreadDeleted }
        : null;

      // Header/analysis identity follows the EXACT row the CTAs act on:
      // `latestLiveDraft` — the same winner `livePendingDraft` above is built
      // from (the carried queue row while it is still live, otherwise the
      // Trash-aware live winner). It degrades to the lead-global newest row
      // ONLY when the lead has no live draft at all, because there is then no
      // CTA to be coherent with and every reader below (campaign lookup,
      // gameplan, disc, enrichment, profile) dereferences it unconditionally.
      // No `carriedLiveDraft ??` prefix is needed: `latestLiveDraft` already
      // begins with one, so the #59 identity-carry contract holds for free.
      // The pre-r3 form (`carriedLiveDraft ?? latest`) sat a sender-A CTA
      // under sender-B's header whenever an UNCARRIED lead's newest row was
      // not the live winner — a newer live draft on a Trashed thread, or a
      // newest row that is already `sent`.
      const headerSource = latestLiveDraft ?? latest;

      // Resolve the name for the exact campaign carried by `headerSource`.
      // A lead can have drafts in more than one campaign, so looking up an
      // unrelated latest/list campaign would make the link text disagree
      // with the href below. `os_campaign_stats` is the canonical,
      // drafts-derived campaign-name domain shared with the Reply Queue.
      const { data: campaign, error: campaignError } = await supabase
        .from('os_campaign_stats')
        .select('campaign_name')
        .eq('organization_id', organizationId)
        .eq('heyreach_campaign_id', headerSource.heyreach_campaign_id)
        .maybeSingle();

      if (campaignError) throw campaignError;
      const campaignName = campaign?.campaign_name || null;

      const gameplan = extractGameplan(
        asJsonRecord(headerSource.decision_trace),
        asJsonRecord(headerSource.strategy),
        headerSource.methodology_used,
        headerSource.confidence,
      );

      const disc = parseDISC(asJsonRecord(headerSource.analysis_report));

      const enrichment = parseLeadEnrichment(asJsonRecord(headerSource.analysis_report));

      // Aggregate signals across all drafts
      const allSignals: Array<ConversationSignal & { messageIndex: number; timestamp: string }> = [];
      drafts.forEach((d, idx) => {
        const signals = parseSignals(asJsonRecord(d.analysis_report));
        signals.forEach((s) => {
          allSignals.push({ ...s, messageIndex: idx + 1, timestamp: d.incoming_at });
        });
      });

      // Approaches used
      const methodologyCounts = new Map<string, { count: number; nextExists: number }>();
      drafts.forEach((d, idx) => {
        const m = d.methodology_used;
        const entry = methodologyCounts.get(m) || { count: 0, nextExists: 0 };
        entry.count++;
        // If a next message exists, the lead responded
        if (idx < drafts.length - 1 && d.status === 'sent') {
          entry.nextExists++;
        }
        methodologyCounts.set(m, entry);
      });

      const approachesUsed = Array.from(methodologyCounts.entries()).map(([methodology, stats]) => ({
        methodology,
        count: stats.count,
        responseCount: stats.nextExists,
      }));

      // Identity fields come from `headerSource` — the row the CTAs act on,
      // falling back to the lead-global newest row only when no live draft
      // exists. The aggregates below (firstContact/lastActivity/exchangeCount)
      // are DELIBERATELY whole-lead and must NOT move onto `headerSource`:
      // they describe the conversation, not the draft under the buttons.
      const profile: LeadProfileData = {
        leadName: headerSource.lead_name,
        leadTitle: headerSource.lead_title,
        leadCompany: headerSource.lead_company,
        leadLinkedinUrl: headerSource.lead_linkedin_url,
        heyreachCampaignId: headerSource.heyreach_campaign_id,
        firstContact: drafts[0]?.incoming_at ?? latest.incoming_at,
        lastActivity: latest.created_at,
        exchangeCount: drafts.length,
      };

      return {
        profile,
        campaignName,
        gameplan,
        disc,
        enrichment,
        signals: allSignals,
        timeline,
        approachesUsed,
        livePendingDraft,
        didFallbackFromCarriedDraft,
      };
    },
    enabled: !!heyreachLeadId && userId !== null && organizationId !== null,
    staleTime: 15000,
  });
};

/** The ONE definition of this cache key, exported so a test asserts against the key the hook
 * actually uses rather than a hand-copied literal that can drift while staying green. */
export const leadTimelineQueryKey = (
  userId: string | null,
  organizationId: string | null,
  heyreachLeadId: string | undefined
): readonly unknown[] => ['lead-timeline', userId, organizationId, heyreachLeadId];

export const useLeadTimeline = (heyreachLeadId: string | undefined) => {
  const { user, organizationId } = useAuth();
  const userId = user?.id ?? null;

  return useQuery<TimelineEntry[]>({
    // Tenancy sweep: `heyreach_lead_id` is a HeyReach-GLOBAL identifier, so it is the one id on this
    // surface that can genuinely collide across organizations — which made an unscoped key the
    // highest-risk entry in the sweep. Identity now keys the cache and an explicit predicate scopes
    // the read; RLS was the only boundary before, and RLS does not partition a browser cache.
    queryKey: leadTimelineQueryKey(userId, organizationId, heyreachLeadId),
    queryFn: async () => {
      if (!heyreachLeadId || organizationId === null) return [];

      const { data, error } = await supabase
        .from('os_linkedin_drafts')
        .select('id, incoming_message, incoming_at, draft_response, edited_response, methodology_used, confidence, status, is_superseded, created_at, sent_at, approved_at')
        .eq('heyreach_lead_id', heyreachLeadId)
        .eq('organization_id', organizationId)
        .order('incoming_at', { ascending: true });

      if (error) throw error;

      return (data || []).map((d): TimelineEntry => ({
        id: d.id,
        incomingMessage: d.incoming_message,
        incomingAt: d.incoming_at,
        draftResponse: d.draft_response,
        editedResponse: d.edited_response,
        status: d.status,
        isSuperseded: d.is_superseded === true,
        methodology: d.methodology_used,
        confidence: d.confidence,
        sentAt: d.sent_at,
        approvedAt: d.approved_at,
        createdAt: d.created_at,
      }));
    },
    enabled: !!heyreachLeadId && userId !== null && organizationId !== null,
    staleTime: 15000,
  });
};
